<template>
    <div class="app-container">
        <div
            id="screen-reader-global-messages"
            class="sr-only"
            role="status"
            aria-atomic="true"
            :aria-live="ariaLiveMode">
            {{ activeScreenReaderMessage }}
        </div>
        <template v-if="isAuthenticated">
            <global-banner
                :is-impersonating="isImpersonating"
                :home-subscription="homeSubscription" />
            <div
                class="app-content"
                :class="{ 'app-content--new-ui': newUiShellActive }">
                <side-nav v-if="showNavItems" />
                <div
                    id="main-container"
                    :class="mainContainerClasses">
                    <top-nav
                        v-if="showNavItems"
                        :is-impersonating="isImpersonating"
                        :home-subscription="homeSubscription" />
                    <vue-wrapper />
                </div>
            </div>
            <impersonation-switcher v-if="hasSuperAccess" />
        </template>
        <template v-else>
            <router-view />
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { i18n } from '@/utils/i18n';
import { showCelebrationModalOnPendoReady } from '@/free/free.utils.js';
import { buildUnauthenticatedLoginRedirectRoute } from '@/vue-router/route-guards.utils';
import { shouldShowNavItems } from '@/vue-router/route.utils';
import { watch, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import GlobalBanner from '@/global-banner/global-banner.vue';
import SideNav from '@/side-nav/side-nav.vue';
import VueWrapper from '@/vue-router/vue-wrapper.vue';
import TopNav from '@/top-nav/top-nav.vue';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';
import useUiVersion from '@/common/use/use-ui-version/use-ui-version';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';
import { useHeadingScope } from '@pendo/components';

export default {
    name: 'App',
    i18n,
    components: {
        SideNav,
        VueWrapper,
        TopNav,
        GlobalBanner,
        ImpersonationSwitcher: defineAsyncComponent(() => import('@/super/impersonation-switcher/impersonation-switcher.vue')),
    },
    setup () {
        useHeadingScope({ level: 1 });

        const { initializeDarkMode } = useDarkMode();
        const { initializeUiVersion, isNewUi } = useUiVersion();
        const { activeScreenReaderMessage, ariaLiveMode, clearScreenReaderQueue: clearQueue } = useAriaLiveRegistry();
        const route = useRoute();

        watch(() => route.fullPath, () => {
            clearQueue();
        });

        return { initializeDarkMode, initializeUiVersion, isNewUi, activeScreenReaderMessage, ariaLiveMode };
    },
    data () {
        return {
            removeCompletedLoadWatch: null,
        };
    },
    computed: {
        ...mapGetters({
            hasCompletedUserLoad: 'auth/hasCompletedUserLoad',
            isAuthenticated: 'auth/isAuthenticated',
            isImpersonating: 'auth/isImpersonating',
            hasSuperAccess: 'auth/hasSuperAccess',
            hasOnlyTrainingSub: 'auth/hasOnlyTrainingSub',
            hasOnlyNovusSub: 'auth/hasOnlyNovusSub',
            isAutoProvisionedTrialExpired: 'subscription/isAutoProvisionedTrialExpired',
            isPendoFree: 'subscription/isPendoFree',
            getFeatureFlag: 'subscription/getFeatureFlag',
            subscriptionList: 'subscription/list',
        }),
        ...mapState({
            activeSubscription: (state) => state.subscription.active,
        }),
        homeSubscription () {
            if (!this.subscriptionList?.length) return null;

            return this.subscriptionList[0];
        },
        eventCount () {
            return this.activeSubscription?.eventCount || 0;
        },
        showNavItems () {
            return shouldShowNavItems(this.$route, this.isAuthenticated);
        },
        newUiShellActive () {
            return this.isNewUi && this.showNavItems;
        },
        mainContainerClasses () {
            return {
                'main-container--new-ui': this.newUiShellActive,
            };
        },
    },
    mounted () {
        this.scrollToHashAnchor();
        this.removeCompletedLoadWatch = this.$watch(
            () => this.hasCompletedUserLoad,
            this.onCompletedUserLoad.bind(this),
            { immediate: true },
        );
    },
    methods: {
        ...mapActions({
            initUserJourney: 'userJourney/init',
            updateEventCount: 'subscription/updateEventCount',
            updateSetting: 'subscription/updateSetting',
        }),
        scrollToHashAnchor () {
            const anchor = window.location.hash;
            if (anchor) {
                const anchorElement = document.querySelector(anchor);
                if (anchorElement) anchorElement.scrollIntoView();
            }
        },
        onCompletedUserLoad (hasCompletedLoading) {
            if (!hasCompletedLoading) return;

            if (!this.isAuthenticated) {
                if (this.$route?.meta?.skipLoginRedirect) return;

                const loginRoute = buildUnauthenticatedLoginRedirectRoute();
                this.$router.replace(loginRoute);

                return;
            }

            this.initializeDarkMode();
            this.initializeUiVersion();

            if (this.hasOnlyNovusSub) {
                this.$router.replace({ name: 'NovusOnly' });

                return;
            }

            if (this.hasOnlyTrainingSub) {
                this.$router.replace({ name: 'Inactive' });

                return;
            }

            if (this.isAutoProvisionedTrialExpired) {
                this.$router.replace({ name: 'TrialExpired' });

                return;
            }

            // if the subscription should lock users out until they've installed the snippet,
            // send them directly to the /setup route
            if (!this.hasSuperAccess && !this.getFeatureFlag('allowSignedMetadata')) {
                const noEvents = this.eventCount === 0;
                const canBypassLockScreen = this.getFeatureFlag('bypassLockScreen');

                if (noEvents && !canBypassLockScreen) {
                    const { setupKey } = this.activeSubscription;

                    return {
                        name: 'Setup',
                        query: {
                            setupKey,
                        },
                    };
                }
            }

            if (this.$route.name === 'TrialExpired') {
                this.$router.replace({ name: 'Home' });

                return;
            }

            if (this.isPendoFree && this.eventCount === 0) {
                this.pollForInstallSuccess();
            }

            // because we eager watch for auth loading, sometimes its already done the first
            // time this watch gets called. In that case, this.removeCompletedLoadWatch hasn't
            // actually been set yet, so queue the unwatch
            this.$nextTick(() => {
                this.removeCompletedLoadWatch();
                delete this.removeCompletedLoadWatch;
            });
        },
        pollForInstallSuccess () {
            if (this.eventCount > 0) {
                showCelebrationModalOnPendoReady(() => {
                    this.updateSetting({
                        setting: 'seenCelebrationModal',
                        value: 'true',
                        label: 'seenCelebrationModal',
                    });
                });

                this.initUserJourney();
            } else {
                setTimeout(async () => {
                    await this.updateEventCount();
                    this.pollForInstallSuccess();
                }, 60000);
            }
        },
    },
};
</script>

<style lang="less" scoped>
.app-content {
    display: flex;
    position: relative;
    overflow: hidden;
    flex-grow: 1;

    &--new-ui {
        --new-ui-shell-margin: 20px;
        background-color: var(--surface-secondary-background);

        #main-container {
            flex: 1 1 auto;
            min-width: 0;
            width: auto;
            margin: var(--new-ui-shell-margin);
            border: 1px solid var(--surface-default-border);
            border-radius: 12px;
            height: auto;
            min-height: 0;
        }

        .side-nav + #main-container {
            margin-left: 0;
        }

        .screen-size(@zero, @large, {
            .side-nav + #main-container {
                margin-left: var(--new-ui-shell-margin);
            }
        });
    }
}
</style>
